import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import formationService from '../../services/formationService';
import {
  ArrowLeft,
  Clock,
  User,
  Users,
  Building2,
  Info,
  Package,
  Rocket,
  CheckCircle2,
  ChevronRight,
  Edit,
  History
} from 'lucide-react';
import HistoryDialog from '../../components/HistoryDialog/HistoryDialog';
import EditFormationDialog from '../../components/EditFormationDialog/EditFormationDialog';
import { formatMoney } from '../../utils/money';
import { formatConsommableRule, getDisplayType } from '../../utils/consommableQuantity';
import './FormationDetailPage.css';

const FormationDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [formation, setFormation] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isResponsable, isAdmin } = useAuth();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const toMoney = (value, currency) => formatMoney(value, { locale: i18n.language, currency });

  const fetchFormation = async () => {
    try {
      const response = await formationService.getById(id);
      setFormation(response);
    } catch (error) {
      console.error('Error fetching formation:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormation();
  }, [id]);

  const handleFormationUpdated = () => {
    fetchFormation();
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('formations.detail.loading')}</span>
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="empty-state">
        <h3>{t('formations.detail.notFound')}</h3>
        <Link to="/formations" className="btn btn-secondary">{t('formations.detail.backToCatalog')}</Link>
      </div>
    );
  }

  const activeConsommables = formation.consommables?.filter(c => c.is_active) || [];
  const consommablesFixes = activeConsommables.filter(c => getDisplayType(c.type_consommable) === 'FIXE');
  const consommablesVariables = activeConsommables.filter(c => getDisplayType(c.type_consommable) !== 'FIXE');
  const trainersDetails = Array.isArray(formation.trainers_details) ? formation.trainers_details : [];
  const trainersIds = Array.isArray(formation.trainers) ? formation.trainers : [];

  return (
    <div className="formation-detail-wrapper">
      <Link to="/formations" className="back-link">
        <ArrowLeft size={16} />
        {t('formations.detail.backToCatalog')}
      </Link>

      <div className="formation-detail-container">
        <div className="detail-header">
          <div className="header-main">
            <h1 className="detail-title">{formation.title}</h1>
            <div className="status-container">
              <span className={`status-badge ${formation.is_active ? 'status-validated' : 'status-cancelled'}`}>
                {formation.is_active ? t('formations.detail.catalogActive') : t('status.inactive')}
              </span>
            </div>
          </div>

          <div className="header-actions">
            {isAdmin && (
              <button type="button" className="btn-edit" onClick={() => setIsEditDialogOpen(true)}>
                <Edit size={18} />
                {t('common.edit')}
              </button>
            )}

            <button type="button" className="btn-edit" onClick={() => setIsHistoryOpen(true)}>
              <History size={18} />
              {t('common.history')}
            </button>

            {(isResponsable || isAdmin) && (
              <Link to={`/sessions/new?formation=${id}`} className="btn-launch">
                <Rocket size={20} />
                {t('formations.detail.launchSession')}
              </Link>
            )}
          </div>
        </div>

        <HistoryDialog
          isOpen={isHistoryOpen}
          onClose={() => setIsHistoryOpen(false)}
          title={t('history.title')}
          appLabel="catalog"
          model="formation"
          objectId={id}
        />

        <EditFormationDialog
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onFormationUpdated={handleFormationUpdated}
          formationToEdit={formation}
        />

        <div className="detail-grid">
          {/* Row 1 - Info cards: Intervenant, Durée, Responsable */}
          <div className="detail-info-row">
            <div className="info-card-compact">
              <div className="info-card-icon"><Users size={20} /></div>
              <div className="info-card-content">
                <label>{t('formations.detail.assignedTrainers')}</label>
                {trainersDetails.length > 0 ? (
                  <div className="trainers-badges">
                    {trainersDetails.map(tn => (
                      <span key={tn.id} className="trainer-badge">
                        {tn.first_name} {tn.last_name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span>{trainersIds.length ? t('formations.detail.trainersCount', { count: trainersIds.length }) : t('common.none')}</span>
                )}
              </div>
            </div>

            <div className="info-card-compact">
              <div className="info-card-icon"><Clock size={20} /></div>
              <div className="info-card-content">
                <label>{t('formations.detail.duration')}</label>
                <span>{t('common.daysCount', { count: formation.duration_days })}</span>
              </div>
            </div>

            <div className="info-card-compact">
              <div className="info-card-icon"><User size={20} /></div>
              <div className="info-card-content">
                <label>{t('formations.detail.owner')}</label>
                <span>{formation.created_by_name}</span>
              </div>
            </div>
          </div>

          {/* Row 2 - Description compact */}
          <div className="detail-description-compact">
            <div className="description-box">
              <h3><Info size={16} /> {t('formations.detail.descriptionTitle')}</h3>
              <p>{formation.description || t('formations.detail.noDescription')}</p>
            </div>
          </div>

          {/* Row 3 - Resources */}
          <div className="detail-resources-fullwidth">
            <h2 className="section-title"><Package size={18} /> {t('formations.detail.resourcesTitle')}</h2>
            <div className="resources-list">
              {consommablesFixes.length > 0 && (
                <div className="resource-group">
                  <h3>{t('formations.detail.fixedCharges')} <span className="type-label">{t('formations.detail.perFormation')}</span></h3>
                  {consommablesFixes.map(c => (
                    <div key={c.id} className="resource-item">
                      <div className="resource-info">
                        <span className="resource-name">{c.name}</span>
                        <span className="resource-supplier">{c.supplier_details?.name}</span>
                      </div>
                      <div className="resource-math">
                        <span className="qty">{c.quantity_fixe}</span>
                        <span className="price">{toMoney(c.unit_price, c.effective_devise)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {consommablesVariables.length > 0 && (
                <div className="resource-group">
                  <h3>{t('formations.detail.variableCharges')}</h3>
                  {consommablesVariables.map(c => (
                    <div key={c.id} className="resource-item">
                      <div className="resource-info">
                        <span className="resource-name">{c.name}</span>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <span className={`type-badge type-${getDisplayType(c.type_consommable).toLowerCase()}`} style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}>
                            {t(`consommables.types.${getDisplayType(c.type_consommable)}`)}
                          </span>
                          <span className="resource-supplier">{c.supplier_details?.name}</span>
                        </div>
                      </div>
                      <div className="resource-math">
                        <span className="qty">
                          {formatConsommableRule(c, t)}
                          {c.is_per_day && ` x ${formation.duration_days}${t('common.daysShort')}`}
                        </span>
                        <span className="price">{toMoney(c.unit_price, c.effective_devise)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formation.consommables?.length === 0 && (
                <div className="empty-resources">
                  <CheckCircle2 size={32} />
                  <p>{t('formations.detail.noSupplies')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormationDetailPage;

